import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";

/** Script público do widget de acessibilidade (UserWay). */
export const ACCESSIBILITY_WIDGET_SRC = "https://cdn.userway.org/widget.js";

/** Id usado para garantir idempotência da injeção. */
export const ACCESSIBILITY_WIDGET_SCRIPT_ID = "lkt-accessibility-widget";

export interface AccessibilityWidgetOptions {
  /**
   * Identificador público da conta do widget. Vazio/undefined = widget não carrega.
   * Configurado por ambiente (`sharedEnvironment.userwayAccountId`).
   */
  readonly accountId?: string;
  /**
   * Posição do gatilho, conforme a documentação do provedor (`data-position`).
   * Só definir quando houver colisão comprovada com elementos fixos do shell.
   */
  readonly position?: string;
}

/**
 * Injeta o script do widget de acessibilidade no documento.
 *
 * Contrato: idempotente, no-op fora do browser e no-op sem `accountId`.
 */
@Injectable({ providedIn: "root" })
export class AccessibilityWidgetService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  public load(options: AccessibilityWidgetOptions): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const accountId = options.accountId?.trim();
    if (!accountId) {
      return;
    }

    if (this.document.getElementById(ACCESSIBILITY_WIDGET_SCRIPT_ID)) {
      return;
    }

    const host = this.document.body ?? this.document.head;
    if (!host) {
      return;
    }

    const script = this.document.createElement("script");
    script.id = ACCESSIBILITY_WIDGET_SCRIPT_ID;
    script.setAttribute("data-account", accountId);
    if (options.position) {
      script.setAttribute("data-position", options.position);
    }
    script.setAttribute("src", ACCESSIBILITY_WIDGET_SRC);
    host.appendChild(script);
  }
}
